import type { FC, ReactNode, RefObject } from 'react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import type { AnimalSpecies } from '@/model/Animal.model';
import type { MetadataField } from '@/model/Metadata.model';
import { featuresSelector } from '@/support/Selectors';

import SpreadsheetV16 from '@/components/UI/SpreadSheet/SpreadsheetV16';
import type { SpreadsheetV16Settings } from '@/components/UI/SpreadSheet/SpreadsheetV16.model';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';
import { defaultMetadataToColumnSettingMapperV16 } from '@/support/hot-v16';
import type { EntityFieldRequiredMap } from '@/utils/entitySchema/useEntitySchema';

import { initAnimalSettingsV16 } from './../Colony.utils.v16';

const unknownSexSelector = createSelector([featuresSelector], (features) => ({
  unknownSexEnabled: features?.unknown_sex === true,
}));

interface AnimalsTableV16Props {
  data: Array<Record<string, unknown>>;
  innerRef: RefObject<HotTableRef>;
  metadata: Array<MetadataField>;
  setMetadata: (metadata: Array<MetadataField>) => void;
  setInvalid: (invalid: boolean) => void;
  overlay?: ReactNode;
  maxRows?: number;
  species: Array<AnimalSpecies>;
  entityFieldRequiredMap: EntityFieldRequiredMap;
  existingSettings?: SpreadsheetV16Settings;
  setSettings?: (settings: SpreadsheetV16Settings) => void;
}

const AnimalsTableV16: FC<AnimalsTableV16Props> = ({
  data,
  innerRef,
  metadata,
  setMetadata,
  setInvalid,
  overlay,
  maxRows,
  species,
  entityFieldRequiredMap,
  existingSettings,
  setSettings,
}) => {
  const { unknownSexEnabled } = useSelector(unknownSexSelector);
  const settings = useMemo<SpreadsheetV16Settings>(
    () =>
      initAnimalSettingsV16(
        species,
        true,
        false,
        undefined,
        undefined,
        existingSettings,
        entityFieldRequiredMap,
        unknownSexEnabled
      ).settings,
    [species, entityFieldRequiredMap, existingSettings, unknownSexEnabled]
  );

  return (
    <SpreadsheetV16
      className="mt3"
      testPrefix="colony-creation-animals-v16"
      data={data}
      innerRef={innerRef}
      settings={settings}
      setSettings={setSettings}
      metadata={metadata}
      setMetadata={setMetadata}
      metadataToColumnSettingMapper={defaultMetadataToColumnSettingMapperV16}
      setInvalid={setInvalid}
      overlay={overlay}
      maxRows={maxRows}
    />
  );
};

export default AnimalsTableV16;
